import type { ServiceRequestActions } from '../model/types';

/**
 * Business logic for service request row-level actions.
 * These are pure functions that determine which contextual actions are allowed
 * for a single service request row.
 *
 * The backend is the source of truth: every row carries an `actions` object
 * ({ canAccept, canReject, ... }) already computed server-side from the request
 * status and the user's context. The client only combines those flags with the
 * global read-only mode — it does not re-derive availability from the status code.
 */

/**
 * Resolved permissions for the contextual actions shown on a service request row.
 */
export interface ServiceRequestActionPermissions {
  canMoveToConsignment: boolean;
  canReject: boolean;
  canPrint: boolean;
}

/**
 * Calculate which row-level actions are allowed for a service request.
 * @param actions - server-provided per-row action flags
 * @param isReadOnly - global read-only mode for the current project/site context
 */
export function getServiceRequestActionPermissions(
  actions: ServiceRequestActions,
  isReadOnly: boolean
): ServiceRequestActionPermissions {
  // In read-only mode nothing mutating is allowed, but printing stays available.
  const editable = !isReadOnly;

  return {
    canMoveToConsignment: editable && actions.canAccept && actions.acceptableInLocationGroup,
    canReject: editable && actions.canReject,
    canPrint: true
  };
}
